import ProfessionalReferences from "@src/app/professionalReferences";
import { ClipboardOrientationLandingPage } from "@src/appV2/ClipboardOrientation/components/LandingPage";
import { ScheduledOrientationOnboarding } from "@src/appV2/ClipboardOrientation/components/Onboarding/ScheduledOrientationOnboarding";
import { ScheduledOrientationOnboardingConfirmation } from "@src/appV2/ClipboardOrientation/components/Onboarding/ScheduledOrientationOnboardingConfirmation";
import { OrientationRescheduled } from "@src/appV2/ClipboardOrientation/components/OrientationRescheduled";
import { CLIPBOARD_ORIENTATION_RESCHEDULED_PATH } from "@src/appV2/ClipboardOrientation/paths";
import { CbhFeatureFlag, EnableReferralSignupScreenVariants } from "@src/appV2/FeatureFlags";
import { InterestedWorkType } from "@src/appV2/redesign/Onboarding/components/InterestedWorkType";
import { WelcomePage } from "@src/appV2/redesign/Onboarding/components/WelcomePage";
import { OnboardingRolesContainer } from "@src/appV2/redesign/Onboarding/Roles/Container";
import { useIsSurfaceShiftsEnabled } from "@src/appV2/redesign/ShiftDiscovery/useIsSurfaceShiftsEnabled";
import { useSkillsAssessmentFeatureFlags } from "@src/appV2/SkillsAssessment/api/useSkillsAssessmentFeatureFlags";
import { SkillsAssessmentMyAccountPage } from "@src/appV2/SkillsAssessment/MyAccountPage";
import { SKILLS_ASSESSMENT_PATH } from "@src/appV2/SkillsAssessment/paths";
import { Redirect, Route, Switch, useRouteMatch } from "react-router-dom";

import {
  AgentAddress,
  AgentDob,
  AgentEmail,
  AgentFirstName,
  AgentLastName,
  AgentOnboardingLicense,
  AgentPotentialShifts,
  AgentQualification,
  AgentSSN,
  AgentSignUpInfo1,
  AgentSignUpInfo2,
  AgentSignUpInfo3,
  ScheduledAssessmentOnboarding,
  ScheduledAssessmentOnboardingConfirmation,
  WelcomeAgent,
} from "./components";
import { AgentProfileErrorModal } from "./components/AgentProfileError/AgentProfileErrorModal";
import { AgentUploadDocuments } from "./components/AgentUploadDocuments";
import { ContractorAgreementReview } from "./components/ContractorAgreementReview";
import { ProceedToRequirementInfo } from "./components/ProceedToRequirementsInfo";
import SignUpBasedReferrals from "./components/SignUpBasedReferrals/SignUpBasedReferrals";
import { SkillsAssessmentOnboarding } from "./components/SkillAssessmentOnboarding";
import { StripeOnboarding } from "./components/StripeOnboarding";
import { OnBoardingRouteProps, STAGES } from "./model";
import { OnboardToStripe } from "../onboardingStripe";
import { OnboardingRouterPath } from "../routing/constant/onboardingRoute";
import { DeprecatedDoNotUseLaunchDarklyFeatureFlagRouteGuard } from "../routing/LaunchDarklyFeatureFlagRouteGaurd/LaunchDarklyFeatureFlagRouteGaurd";
import { VerifyPage } from "../welcome";
import { ProceedToSkillAssessment } from "./components/ProceedToSkillAssessment";

const SIGNUP_BASED_REFERRALS_ENABLED_FLAG_VARIATIONS = [
  {
    key: CbhFeatureFlag.ENABLE_REFERRAL_SIGNUP_SCREEN,
    value: EnableReferralSignupScreenVariants.ON_WITHOUT_CONTACTS,
  },
  {
    key: CbhFeatureFlag.ENABLE_REFERRAL_SIGNUP_SCREEN,
    value: EnableReferralSignupScreenVariants.ON_WITH_CONTACTS,
  },
];

export function OnBoardingRoutes(props: OnBoardingRouteProps) {
  const match = useRouteMatch();
  const { isClipboardOrientationEnabled } = useSkillsAssessmentFeatureFlags();
  const isSurfaceShiftsEnabled = useIsSurfaceShiftsEnabled();

  // Initial onboarding route - where users start the onboarding flow
  const initialOnboardingRoute = OnboardingRouterPath.ONBOARDING_INFO1;

  return (
    <div className="cbhAppV1">
      <AgentProfileErrorModal />
      <Switch>
        <Route
          exact
          path={OnboardingRouterPath.INTERESTED_WORK_TYPE}
          render={() => <InterestedWorkType />}
        />
        <Route
          exact
          path={OnboardingRouterPath.ONBOARDING_INFO1}
          render={() => <AgentSignUpInfo1 {...props} {...STAGES.agentSignUpInfo1} />}
        />
        <Route
          exact
          path={OnboardingRouterPath.ONBOARDING_INFO2}
          render={() => <AgentSignUpInfo2 {...props} {...STAGES.agentSignUpInfo2} />}
        />
        <Route
          exact
          path={OnboardingRouterPath.ONBOARDING_INFO3}
          render={() => <AgentSignUpInfo3 {...props} {...STAGES.agentSignUpInfo3} />}
        />
        <Route
          exact
          path={OnboardingRouterPath.ONBOARDING_ADDRESS}
          render={() => <AgentAddress {...props} {...STAGES.agentAddress} />}
        />
        <Route
          exact
          path={OnboardingRouterPath.ONBOARDING_QUALIFICATION}
          render={() => <AgentQualification {...props} {...STAGES.agentQualification} />}
        />
        <Route
          exact
          path={OnboardingRouterPath.ONBOARDING_LICENSE}
          render={() => {
            return isSurfaceShiftsEnabled ? (
              <OnboardingRolesContainer nextStagePath={STAGES.agentLicense.nextStagePath} />
            ) : (
              <AgentOnboardingLicense {...props} {...STAGES.agentLicense} />
            );
          }}
        />
        <Route
          exact
          path={OnboardingRouterPath.ONBOARDING_FIRST_NAME}
          render={() => <AgentFirstName {...props} {...STAGES.agentFirstName} />}
        />
        <Route
          exact
          path={OnboardingRouterPath.ONBOARDING_LAST_NAME}
          render={() => <AgentLastName {...props} {...STAGES.agentLastName} />}
        />
        <Route
          exact
          path={OnboardingRouterPath.ONBOARDING_EMAIL}
          render={() => <AgentEmail {...props} {...STAGES.agentEmail} />}
        />
        <Route
          exact
          path={OnboardingRouterPath.ONBOARDING_POTENTIAL_SHIFTS}
          render={() => <AgentPotentialShifts {...props} {...STAGES.agentPotentialShifts} />}
        />
        <Route
          exact
          path={OnboardingRouterPath.ONBOARDING_CONTRACTOR_AGREEMENT_REVIEW}
          render={() => (
            <ContractorAgreementReview {...props} {...STAGES.contractorAgreementReview} />
          )}
        />

        <Route
          exact
          path={OnboardingRouterPath.ONBOARDING_CONTRACTOR_AGREEMENT}
          render={() => <OnboardToStripe {...props} {...STAGES.contractorAgreement} />}
        />
        <Route
          exact
          path={OnboardingRouterPath.ONBOARDING_SKILL_ASSESSMENT}
          render={() => <SkillsAssessmentOnboarding {...props} {...STAGES.scheduleAssessment} />}
        />
        <Route
          exact
          path={OnboardingRouterPath.ONBOARDING_SCHEDULED_ASSESSMENT_CONFIRMATION}
          render={() =>
            isClipboardOrientationEnabled ? (
              <ScheduledOrientationOnboardingConfirmation
                {...props}
                {...STAGES.scheduleAssessmentConfirmation}
              />
            ) : (
              <ScheduledAssessmentOnboardingConfirmation
                {...props}
                {...STAGES.scheduleAssessmentConfirmation}
              />
            )
          }
        />
        <Route
          exact
          path={OnboardingRouterPath.ONBOARDING_STRIPE}
          render={() => <StripeOnboarding {...props} {...STAGES.stripeOnboarding} />}
        />
        <Route
          exact
          path={OnboardingRouterPath.READY_TO_EARN}
          render={() => (
            <DeprecatedDoNotUseLaunchDarklyFeatureFlagRouteGuard
              conditionalFlagValues={SIGNUP_BASED_REFERRALS_ENABLED_FLAG_VARIATIONS}
              conditional="OR"
              redirectTo={OnboardingRouterPath.SHOW_FRIENDS_SHIFTS}
              route={OnboardingRouterPath.READY_TO_EARN}
            >
              <SignUpBasedReferrals {...props} {...STAGES.agentReadyToEarn} />
            </DeprecatedDoNotUseLaunchDarklyFeatureFlagRouteGuard>
          )}
        />
        <Redirect
          exact
          path={OnboardingRouterPath.SHOW_FRIENDS_SHIFTS}
          to={OnboardingRouterPath.ONBOARDING_PROCEED_TO_REQUIREMENT_INFO}
        />
        <Route
          exact
          path={OnboardingRouterPath.ONBOARDING_PROCEED_TO_REQUIREMENT_INFO}
          render={() => (
            <ProceedToRequirementInfo {...props} {...STAGES.proceedToRequirementsInfo} />
          )}
        />
        <Route
          exact
          path={OnboardingRouterPath.ONBOARDING_UPLOAD_DOCUMENTS}
          render={() => <AgentUploadDocuments {...props} {...STAGES.uploadDocuments} />}
        />
        <Route
          exact
          path={OnboardingRouterPath.ONBOARDING_PROCEED_TO_SKILL_ASSESSMENT}
          render={() => (
            <ProceedToSkillAssessment {...props} {...STAGES.proceedToSkillAssessment} />
          )}
        />
        <Route
          exact
          path={OnboardingRouterPath.ONBOARDING_SCHEDULED_ASSESSMENT}
          render={() =>
            isClipboardOrientationEnabled ? (
              <ScheduledOrientationOnboarding {...props} {...STAGES.scheduleAssessment} />
            ) : (
              <ScheduledAssessmentOnboarding {...props} {...STAGES.scheduleAssessment} />
            )
          }
        />
        <Route
          exact
          path={OnboardingRouterPath.ONBOARDING_WELCOME}
          render={() => (isSurfaceShiftsEnabled ? <WelcomePage /> : <WelcomeAgent {...props} />)}
        />
        <Route exact path={OnboardingRouterPath.ONBOARDING_REVERIFY} component={VerifyPage} />
        <Route exact path={match.path} render={() => <Redirect to={initialOnboardingRoute} />} />
        <Route
          exact
          path={OnboardingRouterPath.ONBOARDING_SSN}
          render={() => <AgentSSN {...props} {...STAGES.agentSSN} />}
        />
        <Route
          exact
          path={OnboardingRouterPath.ONBOARDING_DOB}
          render={() => <AgentDob {...props} {...STAGES.agentDOB} />}
        />
        <Route
          exact
          path={OnboardingRouterPath.ONBOARDING_PROFESSIONAL_REFERENCES}
          render={() => <ProfessionalReferences {...props} />}
        />
        <Route exact path={`${match.url}/accounts/${SKILLS_ASSESSMENT_PATH}`}>
          {isClipboardOrientationEnabled ? (
            <ClipboardOrientationLandingPage isSignup={true} />
          ) : (
            <SkillsAssessmentMyAccountPage isSignup={true} />
          )}
        </Route>
        <Route exact path={`${match.url}/accounts/${CLIPBOARD_ORIENTATION_RESCHEDULED_PATH}`}>
          {isClipboardOrientationEnabled ? (
            <OrientationRescheduled isSignup={true} />
          ) : (
            <Redirect to={`${match.url}/accounts/${SKILLS_ASSESSMENT_PATH}`} />
          )}
        </Route>
        {/* Catch-all route for any unmatched paths (may happen if coming from a home-v2 route) - redirects to initial onboarding step */}
        <Route render={() => <Redirect to={initialOnboardingRoute} />} />
      </Switch>
    </div>
  );
}
